import { useProjectParams } from '@hooks/useProjectParams';
import { Toolbar } from 'components/Toolbar';
import { WorkOrderConstructionTypesTabSelect } from 'components/WorkOrderConstructionTypesTabSelect';
import { filtersWorkOrderSlice } from 'features/filtersWorkOrder/filtersWorkOrderSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderVersionItem } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { getWorkOrderVersionListItems } from 'pages/Project/Common/utils';
import { Button, cnMixSpace, Grid, GridItem, IconBackward } from 'uikit';
import { isNumber } from 'utils';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { SelectVersionsWorkOrder } from '../../Common/WorkOrderVersionSelect';
import { useConstructionTypes } from '../WorkOrder/hooks/useConstructionTypes';
import { WorkOrderFilter } from '../WorkOrder/WorkOrderFilter';
import { WorkOrderFilterLevelEnum } from '../WorkOrder/WorkOrderFilter/types';
import { TABLE_SETTINGS_HEIGHT } from '../WorkOrder/WorkOrderPrice';
import { workOrderVersionsGql } from '../WorkOrder/workOrderQueries';
import { WorkOrderChapters } from '../WorkOrder/workorderTypes';

import { useWorkOrderVersionObjects } from './hooks/useWorkOrderVersionObjects';
import { useWorkOrderVersionSeasons } from './hooks/useWorkOrderVersionSeasons';
import { WorkOrderVersionRequisites } from './WorkOrderVersionRequisites';
import { WorkOrderVersionSummaryTable } from './WorkOrderVersionSummaryTable';
import { WorkOrderVersionTable } from './WorkOrderVersionTable/WorkOrderVersionTable';

import './WorkOrderVersion.css';

export const WorkOrderVersionNew = () => {
    const { id: projectId, orderId, versionId, methodology } = useProjectParams();
    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);
    const [position, setPosition] = useState<number | undefined>();
    const gridTableRef = useRef<AgGridReact>(null);
    const refBtn = useRef<HTMLButtonElement>(null);
    const { order: orderRules } = useAccess();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state }: { state: { reload: boolean | number; openEditWorkOrder?: boolean } } = useLocation();

    const { projectData, refreshProject } = useProjectData(projectId);
    const getContractData = projectData?.contractNavigation?.data?.contractNavData;
    const statusProject = projectData?.status;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = getContractData?.status === ContractStatusEnum.InImplementation;
    const workOrderId = Number(orderId);
    const workOrderVersionId = Number(versionId);

    const [constructionTypeId, setConstructionTypeId] = useState(0);
    const [filters, setFilters] = useState<any>({});

    const [constructionTypes, constructionTypesStatus] = useConstructionTypes(workOrderId);

    const [workOrderObjects] = useWorkOrderVersionObjects(workOrderVersionId);

    const variables = useMemo(
        () => ({
            workOrderId,
        }),
        [workOrderId],
    );

    const [workOrderVersionsData] = useRequest({
        requestId: 'workOrderVersions',
        query: workOrderVersionsGql,
        variables,
        withEffect: true,
    });

    const versionSelectItems = useMemo(
        () => getWorkOrderVersionListItems(workOrderVersionsData?.uerWorkOrderVersions),
        [workOrderVersionsData?.uerWorkOrderVersions],
    );

    const activeItemVersion = useMemo(
        () => versionSelectItems?.find(({ id }: { id: number }) => id === workOrderVersionId),
        [versionSelectItems, workOrderVersionId],
    );

    useEffect(() => {
        dispatch(filtersWorkOrderSlice.actions.setFiltersWorkOrder(filters));
    }, [filters, dispatch]);

    useEffect(() => {
        if (state?.openEditWorkOrder) {
            setTimeout(() => refBtn.current?.click(), 500);
        }
    }, [state?.openEditWorkOrder]);

    const isSummaryChapterSelected = filters.level?.value === WorkOrderFilterLevelEnum.TOTALS;

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const firstRenderWorkOrderFilterSetter = useCallback(({ savedValuesObj }) => {
        const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
        const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

        if (savedConstructionTypeId) {
            setConstructionTypeId(savedConstructionTypeId);
        }
        if (savedFilters) {
            setFilters(savedFilters);
        }
    }, []);

    const firstRenderWorkOrderEmptyFilterSetter = useMemo(() => {
        if (constructionTypes?.length) {
            return (setter: (params: { key: string | undefined; newState: any }) => void) => {
                const newConstructionTypeId = constructionTypes[0].id;

                setConstructionTypeId(newConstructionTypeId);
                setter({
                    key: 'constructionTypeId',
                    newState: newConstructionTypeId,
                });
            };
        }

        return null;
    }, [constructionTypes]);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: firstRenderWorkOrderFilterSetter,
                withEmptyFilterSetter: firstRenderWorkOrderEmptyFilterSetter,
                unableToProcess: constructionTypesStatus === RequestStatus.FETCHING,
            }),
            [constructionTypesStatus, firstRenderWorkOrderEmptyFilterSetter, firstRenderWorkOrderFilterSetter],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionTypeId) => {
            setConstructionTypeId(newConstructionTypeId);
            setFilters(savedFilterState?.[newConstructionTypeId] ?? {});

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionTypeId,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const handleFilterFormChange = useCallback(
        (newFilters) => {
            let mergedFilters = {};

            setFilters((oldFilters: any) => {
                if (
                    oldFilters.chapter &&
                    oldFilters.chapter !== newFilters.chapter &&
                    newFilters.chapter === WorkOrderChapters.OTHERS
                ) {
                    mergedFilters = { ...oldFilters, ...newFilters, subObjectId: undefined };

                    return mergedFilters;
                }
                if (newFilters.chapter === WorkOrderChapters.SUMMARY) {
                    mergedFilters = { ...newFilters, objectId: undefined, subObjectId: undefined };

                    return mergedFilters;
                }

                mergedFilters = { ...oldFilters, ...newFilters };

                return mergedFilters;
            });

            handleSaveFilterState({
                key: String(constructionTypeId),
                newState: mergedFilters,
            });
        },
        [constructionTypeId, handleSaveFilterState],
    );

    const checkedWorkOrderNavs = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((workOrder) => workOrder.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const titleWorkOrder = activeItemVersion
        ? activeItemVersion.labelWithoutChangeNumber.replace('НЗ', 'Наряд-Заказ')
        : getLabelWorkOrder(checkedWorkOrderNavs);

    const statusWorkOrder = activeItemVersion
        ? (activeItemVersion?.item?.workOrderVersionItem as WorkOrderVersionItem)?.status
        : checkedWorkOrderNavs?.status;

    const handleBackVersion = useCallback(() => {
        navigate(`/projects/${methodology}/${projectId}/order/${orderId}`);
    }, [methodology, navigate, orderId, projectId]);

    const tableRef = useRef<HTMLDivElement>(null);

    const isWorkOrderRequisitesDisabled = !isContractStatusWork || !orderRules.canEdit;

    const workOrderHasInProgress = useMemo<boolean>(
        () => checkedWorkOrderNavs?.metadata?.hasInProgress,
        [checkedWorkOrderNavs?.metadata?.hasInProgress],
    );

    const reloadWorkOrderTable = useCallback(() => {
        if (gridTableRef?.current) {
            gridTableRef?.current?.api?.refreshInfiniteCache?.();
        }
    }, []);

    useEffect(() => {
        if (tableRef.current) {
            setPosition(tableRef.current?.getBoundingClientRect?.()?.top);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableRef, isHeaderOpened, tableRef.current?.getBoundingClientRect?.()?.top]);

    const [seasons] = useWorkOrderVersionSeasons(workOrderVersionId);

    const handleNavigateStructure = useCallback(() => {
        navigate(`/projects/uer/${projectId}/order/${orderId}/version/${versionId}/structure`);
    }, [navigate, orderId, projectId, versionId]);

    return (
        <ProjectPageLayout
            title={titleWorkOrder}
            statusWorkOrder={statusWorkOrder}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            isHeaderCollapsable
            layoutEntityKey={workOrderVersionId}
            headerBottom={
                <Toolbar flex>
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Вернуться к текущей версии"
                        view="secondary"
                        size="s"
                        onClick={handleBackVersion}
                        iconLeft={IconBackward}
                    />
                    <WorkOrderVersionRequisites
                        workOrderId={workOrderVersionId}
                        isDisabled={isWorkOrderRequisitesDisabled || isDisabledByProjectStatus}
                        refBtn={refBtn}
                        activeItemVersion={activeItemVersion}
                    />
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Просмотреть структуру"
                        view="secondary"
                        size="s"
                        onClick={handleNavigateStructure}
                    />
                    {!!workOrderVersionsData?.uerWorkOrderVersions?.length && (
                        <SelectVersionsWorkOrder
                            data={workOrderVersionsData?.uerWorkOrderVersions}
                            activeItemVersion={activeItemVersion}
                        />
                    )}
                </Toolbar>
            }
        >
            <div
                id="workOrder"
                style={
                    {
                        '--table-settings-height': `${TABLE_SETTINGS_HEIGHT}px`,
                        '--others-height': isNumber(position) && `${position + TABLE_SETTINGS_HEIGHT + 20}px`,
                    } as React.CSSProperties
                }
            >
                <WorkOrderConstructionTypesTabSelect
                    onChange={handleConstructionTypeChange}
                    constructionTypes={constructionTypes}
                    activeConstructionTypeId={constructionTypeId}
                />
                <Grid
                    cols={10}
                    gap="xl"
                    rowGap="xl"
                    colGap="xl"
                    className={cnMixSpace({ mT: 'xl', mB: 'xl' })}
                    style={{ overflow: 'auto' }}
                >
                    <GridItem col={6}>
                        <WorkOrderFilter
                            workOrderObjects={workOrderObjects}
                            constructionTypeId={constructionTypeId}
                            defaultValues={filters}
                            onChange={handleFilterFormChange}
                        />
                    </GridItem>
                </Grid>
                <div ref={tableRef}>
                    {!isSummaryChapterSelected && (
                        <WorkOrderVersionTable
                            ref={gridTableRef}
                            workOrderId={workOrderId}
                            objectId={filters?.objectId}
                            subObjectId={filters?.subObjectId}
                            constructionTypeId={constructionTypeId}
                            workOrderStatus={statusWorkOrder}
                            workOrderHasInProgress={workOrderHasInProgress}
                            costRounding={
                                projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0
                            }
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            contractStatus={getContractData?.status}
                            reloadWorkOrderTable={reloadWorkOrderTable}
                            refreshProjectData={refreshProject}
                            versionId={workOrderVersionId}
                            seasons={seasons}
                            workOrderObjects={workOrderObjects}
                        />
                    )}
                    {isSummaryChapterSelected && (
                        <WorkOrderVersionSummaryTable
                            workOrderVersionId={workOrderVersionId}
                            seasons={seasons}
                            costRounding={
                                projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0
                            }
                        />
                    )}
                </div>
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderVersionNew.displayName = 'WorkOrderVersionNew';
